import { type ElasticsearchQuery, type FacetFieldConfig } from '@searchkit/api';

import { REPUBS } from '@cx-modules/constants';

import {
  FILTERS_CONFIGS,
  getPositiveEvSortOrder,
  type PositiveEvSortGroup,
} from '~/apps/product-plus/search-domain/config/filters-config';
import { FOOTBALL } from '~/apps/product-plus/search-domain/constants/categoryGroupNames';
import {
  EVENT_DISPLAY_NAME,
  getEventDisplayNames,
} from '~/apps/product-plus/search-domain/constants/eventDisplayNames';
import {
  allWNBAQuery,
  eplOnlySoccerQuery,
  lineReductionQuery,
  onlyFootballQuery,
  positiveEvQuery,
  supportedCategoryGroupsQuery,
} from '~/apps/product-plus/search-domain/queries';
import {
  type SearchDomainContext,
  type SearchRuntimeSortingConfig,
  type SearchRuntimeSortingOption,
  type SearchUiContext,
} from '~/apps/product-plus/search-domain/types';

export const resultAttributes = [
  'aiProbability',
  'betDescription',
  'betDescriptionMap',
  'betId',
  'betName',
  'betTypeId',
  'bookmakerCode',
  'categoryGroupId',
  'categoryGroupName',
  'categoryId',
  'categoryName',
  'eventDisplayName',
  'eventDisplayNameMap',
  'eventId',
  'expectedValue',
  'expectedValueStatus',
  'line',
  'marketClassification',
  'marketDisplayName',
  'marketDisplayNameMap',
  'marketId',
  'marketName',
  'marketPropType',
  'oddsAmerican',
  'oddsDecimal',
  'oddsFractional',
  'parentBetId',
  'sportsData',
  'sportsbookProbability',
  'subeventId',
  'subeventName',
  'subeventStartDate',
  'subeventStartTime',
  'teamData',
  'type',
];

export function getPositiveEvBaseFilters(context: SearchDomainContext) {
  const filters: ElasticsearchQuery[] = [positiveEvQuery];

  if (context.searchFlags.soccerEplOnly) {
    filters.push(eplOnlySoccerQuery);
  }

  if (context.searchFlags.allWnba) {
    filters.push(allWNBAQuery);
  }

  if (context.repubConfig.name === REPUBS.WS.name) {
    filters.push(onlyFootballQuery);
  }

  if (context.searchFlags.lineReduction) {
    filters.push(lineReductionQuery);
  }

  if (!context.searchFlags.newCategoryGroups) {
    filters.push(supportedCategoryGroupsQuery);
  }

  return filters;
}

export function getPositiveEvFacetAttributes(context: SearchDomainContext): FacetFieldConfig[] {
  const areInternalSearchToolsEnabled = context.searchFlags.internalSearchTools;

  const internalSearchToolFacets: FacetFieldConfig[] = areInternalSearchToolsEnabled
    ? [{ attribute: 'bookmakerCode', field: 'bookmakerCode', type: 'string' }]
    : [];

  const facets = [
    ...internalSearchToolFacets,
    { attribute: 'aiProbability', field: 'aiProbability', type: 'numeric' },
    { attribute: 'eventDisplayName', field: 'eventDisplayName', type: 'string' },
    { attribute: 'eventId', field: 'eventId', type: 'numeric' },
    { attribute: 'expectedValue', field: 'expectedValue', type: 'numeric' },
    { attribute: 'marketPropType', field: 'marketPropType', type: 'string' },
    { attribute: 'marketDisplayNameMap.uk', field: 'marketDisplayNameMap.uk.keyword', type: 'string' },
    { attribute: 'marketDisplayNameMap.us', field: 'marketDisplayNameMap.us.keyword', type: 'string' },
    { attribute: 'marketDisplayNameMap.it', field: 'marketDisplayNameMap.it.keyword', type: 'string' },
    { attribute: 'oddsDecimal', field: 'oddsDecimal', type: 'numeric' },
    { attribute: 'sportsData.participant.name', field: 'sportsData.participant.name.keyword', type: 'string' },
    { attribute: 'sportsData.participant.nickName', field: 'sportsData.participant.nickName.keyword', type: 'string' },
    {
      attribute: 'sportsData.participant.shortName',
      field: 'sportsData.participant.shortName.keyword',
      type: 'string',
    },
    { attribute: 'subeventId', field: 'subeventId', type: 'numeric' },
    { attribute: 'subeventStartDate', field: 'subeventStartDate', type: 'string' },
    { attribute: 'subeventStartDateLocal', field: 'subeventStartDateLocal', type: 'string' },
    { attribute: 'subeventStartTime', field: 'subeventStartTime', type: 'string' },
    { attribute: 'sportsData.historyCount', field: 'sportsData.historyCount', type: 'numeric' },
    { attribute: 'sportsData.hitRates.last10', field: 'sportsData.hitRates.last10', type: 'numeric' },
    { attribute: 'teamData.away.name', field: 'teamData.away.name.keyword', type: 'string' },
    { attribute: 'teamData.away.nickName', field: 'teamData.away.nickName.keyword', type: 'string' },
    { attribute: 'teamData.away.shortName', field: 'teamData.away.shortName.keyword', type: 'string' },
    { attribute: 'teamData.home.name', field: 'teamData.home.name.keyword', type: 'string' },
    { attribute: 'teamData.home.nickName', field: 'teamData.home.nickName.keyword', type: 'string' },
    { attribute: 'teamData.home.shortName', field: 'teamData.home.shortName.keyword', type: 'string' },
    { attribute: 'type', field: 'type', type: 'string' },
  ] satisfies FacetFieldConfig[];

  return facets;
}

export function getPositiveEvSearchSettings(context: SearchUiContext) {
  const repubCountry = context.repubCountry;
  if (!repubCountry) {
    return {
      highlights: [],
      search: [],
    };
  }

  const isMatchCentreTab = context.usageContext === 'match-centre-tab';

  const teamDataAttributes = [
    'teamData.away.name',
    'teamData.away.nickName',
    'teamData.away.shortName',
    'teamData.home.name',
    'teamData.home.nickName',
    'teamData.home.shortName',
  ];

  const searchAttributes = [
    `marketDisplayNameMap.${repubCountry}`,
    'sportsData.participant.name',
    'sportsData.participant.nickName',
    'sportsData.participant.shortName',
  ];

  const searchFacets = ['marketDisplayName', 'eventId'];

  if (!isMatchCentreTab) {
    searchAttributes.push(...teamDataAttributes);
  }

  return {
    highlights: searchAttributes,
    search: searchAttributes,
    facets: searchFacets,
    results: resultAttributes,
  };
}

export function getPositiveEvSorting(
  context: SearchUiContext,
  currentRefinements?: string[][],
): SearchRuntimeSortingConfig {
  const repubName = context.repubConfig.name;
  const translations = FILTERS_CONFIGS[repubName as keyof typeof FILTERS_CONFIGS] ?? FILTERS_CONFIGS.OC;
  const eventDisplayName = currentRefinements
    ?.find(refinement => refinement[0]?.startsWith(`${EVENT_DISPLAY_NAME}:`))?.[0]
    ?.split(':')
    ?.slice(1)
    ?.join(':');

  const repubEventDisplayNames = getEventDisplayNames(repubName);
  const isSoccer =
    !!eventDisplayName &&
    repubEventDisplayNames[eventDisplayName as keyof typeof repubEventDisplayNames]?.categoryGroupName === FOOTBALL;
  const isMatchCentreTab = context.usageContext === 'match-centre-tab';

  const sortGroups: Record<PositiveEvSortGroup, SearchRuntimeSortingOption[]> = {
    aiProbability: [
      { field: 'aiProbability', label: translations.sortAIProbHL, order: 'desc' },
      { field: 'aiProbability', label: translations.sortAIProbLH, order: 'asc' },
    ],
    bestOdds: [
      { field: 'oddsDecimal', label: translations.sortBestOddsHL, order: 'desc' },
      { field: 'oddsDecimal', label: translations.sortBestOddsLH, order: 'asc' },
    ],
    eventTime: isMatchCentreTab
      ? []
      : [{ field: 'subeventStartTime', label: translations.sortEventTime, order: 'asc' }],
    expectedValue: [
      { field: 'expectedValue', label: translations.sortEVHL, order: 'desc' },
      { field: 'expectedValue', label: translations.sortEVLH, order: 'asc' },
    ],
    hitRate: [
      { field: 'sportsData.hitRates.last10', label: translations.sortHitRate10HL, order: 'desc' },
      { field: 'sportsData.hitRates.last10', label: translations.sortHitRate10LH, order: 'asc' },
    ],
    sportsbookProbability: [
      { field: 'sportsbookProbability', label: translations.sortImProbHL, order: 'desc' },
      { field: 'sportsbookProbability', label: translations.sortImProbLH, order: 'asc' },
    ],
  };

  const sortOrder = getPositiveEvSortOrder(repubName);
  const options = sortOrder.flatMap(group => sortGroups[group]);

  const defaultOverride = isSoccer
    ? ({ field: 'sportsbookProbability', label: translations.sortImProbHL, order: 'desc' } as const)
    : undefined;

  return { defaultOverride, options };
}
